/**
 * A simple cache for storing signed image URLs to avoid repeated requests
 */

interface CacheEntry {
  url: string;
  timestamp: number;
  expiresAt: number; // Timestamp when the URL expires
}

class ImageCache {
  private cache: Record<string, CacheEntry> = {};
  private readonly DEFAULT_EXPIRY_MS = 3600000; // 1 hour default expiry

  /**
   * Set a signed URL in the cache with expiration
   */
  set(key: string, url: string, expiryMs = this.DEFAULT_EXPIRY_MS): void {
    const now = Date.now();
    this.cache[key] = {
      url,
      timestamp: now,
      expiresAt: now + expiryMs
    };
  }

  /**
   * Get a signed URL from the cache if it exists and hasn't expired
   */
  get(key: string): string | null {
    const entry = this.cache[key];
    if (!entry) return null;

    // Check if the entry has expired
    if (Date.now() > entry.expiresAt) {
      delete this.cache[key];
      return null;
    }

    return entry.url;
  }

  /**
   * Clear the entire cache or a specific key
   */
  clear(key?: string): void {
    if (key) {
      delete this.cache[key];
    } else {
      this.cache = {};
    }
  }

  /**
   * Check if a key exists in the cache and is valid
   */
  has(key: string): boolean {
    const entry = this.cache[key];
    if (!entry) return false;
    
    // Check if the entry has expired
    if (Date.now() > entry.expiresAt) {
      delete this.cache[key];
      return false;
    }
    
    return true;
  }
}

// Export a singleton instance
export const imageCache = new ImageCache();
